import { IN_BRANCH_SIGNUP } from 'src/features/constants';
import { redirectToLogin } from 'src/session';
import { isNotEmpty } from 'src/utilities/hasValue';
import {
  authentication,
  authenticatedUser,
  unAuthenticatedUser,
  authenticatedApplicantContext,
  currentAppSession,
  parseData
} from 'src/utilities/storage';
import { reducerWithInitialState } from 'typescript-fsa-reducers';
import {
  handleInitCurrentLoanContext,
  handleInitTokens,
  handleLogout,
  handleRefreshTokens,
  handleStartSession,
  handleImpersonateSession
} from './authentication.actions';
import { clearLocalStorage } from './authentication.helpers';
import { State } from './index';
import { equals, includes } from 'ramda';
import { COOKIE } from 'src/services/auth/auth';
import SiteSetting from 'src/siteSetting';
import { USER_ONBOARD_APPLICANT } from 'src/services/constants/accounts';

export const defaultState: State = {
  accountId: null,
  accountEmail: null,
  accountName: null,
  loggedInRole: null,
  token: null,
  expiration: null,
  advisorUsername: null,
  signupType: null,
  teamId: null,
  applicantContext: {
    accountId: null,
    accountEmail: null,
    teamId: null
  }
};

const reducer = reducerWithInitialState(defaultState)
  .case(handleStartSession, (state, payload) => {
    const {
      accountId,
      accountEmail,
      accountName,
      loggedInRole,
      token,
      expires,
      teamId,
      advisorUsername,
      productType,
      rolesId,
      signupType
    } = payload;
    /** set local storage */

    authenticatedUser.accountId.set(accountId || '');
    authenticatedUser.accountEmail.set(accountEmail || '');
    authenticatedUser.accountName.set(accountName || '');
    authenticatedUser.loggedInRole.set(loggedInRole || '');
    authenticatedUser.rolesId.set(JSON.stringify(rolesId));
    authentication.token.set(token || '');
    authentication.expire.set(expires || '');
    unAuthenticatedUser.advisorUsername.set(advisorUsername || '');
    productType || !unAuthenticatedUser.productType.get() ? unAuthenticatedUser.productType.set(productType || ''): '';
    signupType && unAuthenticatedUser.signupType.set(signupType)
    authenticatedUser.teamId.set(teamId || '')

    const applicantContext = {
      accountId,
      accountEmail,
      rolesId,
      teamId
    }

    const isApplicant = includes(USER_ONBOARD_APPLICANT, rolesId)

    isApplicant && authenticatedApplicantContext.applicantContext.set(
      window.btoa(JSON.stringify(applicantContext)));

    /** set redux state */
    return {
      ...state,
      accountId: accountId || null,
      accountEmail: accountEmail || null,
      accountName: accountName || null,
      loggedInRole: loggedInRole || null,
      advisorUsername: advisorUsername || null,
      token: token || null,
      expiration: expires || null,
      productType: productType || null,
      teamId: teamId || null,
      rolesId: rolesId,
      signupType,
      applicantContext
    };
  })
  .case(handleImpersonateSession, (state, payload) => {

    const {
      impersonator,
      impersonatedUser
    } = payload;

    const {
      accountId,
      accountEmail,
      accountName,
      loggedInRole,
      token,
      expires,
      teamId,
      rolesId
    } = impersonator;

    const {
      applicantAccountId,
      applicantAccountEmail,
      applicantTeamId,
      advisorUsername,
      productType,
      applicantRolesId,
      signupType
    } = impersonatedUser;

    /** set local storage */
    authenticatedUser.accountId.set(accountId || '');
    authenticatedUser.accountEmail.set(accountEmail || '');
    authenticatedUser.accountName.set(accountName || '');
    authenticatedUser.loggedInRole.set(loggedInRole || '');
    authenticatedUser.rolesId.set(JSON.stringify(rolesId));
    authentication.token.set(token || '');
    authentication.expire.set(expires || '');
    unAuthenticatedUser.advisorUsername.set(advisorUsername || '');
    productType || !unAuthenticatedUser.productType.get() ? unAuthenticatedUser.productType.set(productType || ''): '';
    unAuthenticatedUser.signupType.set(IN_BRANCH_SIGNUP)
    authenticatedUser.teamId.set(teamId || '')

    const applicantContext = {
      accountId: applicantAccountId,
      accountEmail: applicantAccountEmail,
      teamId: applicantTeamId
    }

    const isApplicant = includes(USER_ONBOARD_APPLICANT, applicantRolesId)
    isApplicant && authenticatedApplicantContext.applicantContext.set(
      window.btoa(JSON.stringify(applicantContext)));

    /** set redux state */
    return {
      ...state,
      accountId: accountId || null,
      accountEmail: accountEmail || null,
      accountName: accountName || null,
      loggedInRole: loggedInRole || null,
      advisorUsername: advisorUsername || null,
      token: token || null,
      expiration: expires || null,
      productType: productType || null,
      teamId: teamId || null,
      rolesId: rolesId,
      signupType,
      applicantContext
    };

  })
  .case(handleInitTokens, (state) => {
    /**
     * if our token is expired, clear local storage
     * and redux state
     */
    const expiryDateFromLocalStorage = authentication.expire.get();
    const expiryDate = new Date(expiryDateFromLocalStorage);
    if (expiryDateFromLocalStorage && expiryDate < new Date()) {
      /**
       * the case where the user refreshes the page and has a expiry time in localstorage
       * but it's  expired
       */
      redirectToLogin(location.pathname, location.search);
      return {
        ...state,
        accountId: null,
        accountEmail: null,
        accountName: null,
        applicantContext: {
          accountId: null,
          accountEmail: null,
          teamId: null
        },
        loggedInRole: null,
        token: null,
        expiration: null,
        advisorUsername: null,
        productType: null,
        rolesId: null,
        signupType: null,
        teamId: null
      };
    }
    /** the account ie */
    const accountId = authenticatedUser.accountId.get();

    /** the account email*/
    const accountEmail = authenticatedUser.accountEmail.get();

    /** the account email*/
    const accountName = authenticatedUser.accountName.get();

    const rolesId = authenticatedUser.rolesId.get();

    /**
     * otherwise just set redux state to what's in local storage
     * currently - may be null value here but that's okay
     */
    const token = authentication.token.get();
    const hasAth = isNotEmpty(token) || (isNotEmpty(accountId) && equals(SiteSetting.getCoreAPIAuthProvider(), COOKIE))

    /** if we have no token in local storage, send us to login */
    if (!hasAth) {
      redirectToLogin(location.pathname, location.search);
    }

    /** the applicant context  */
    const applicantContextDecoded = isNotEmpty(authenticatedApplicantContext.applicantContext.get()) ?
    decodeURIComponent(window.atob(authenticatedApplicantContext.applicantContext.get())): null;
    const applicantContext: any = parseData(applicantContextDecoded);


    /** the account role*/
    const loggedInRole = authenticatedUser.loggedInRole.get();

    const advisorUsername = unAuthenticatedUser.advisorUsername.get();

    const productType = unAuthenticatedUser.productType.get();


    const teamId = authenticatedUser.teamId.get();
    const currentLoanRoleContext = isNotEmpty(authenticatedApplicantContext.currentLoanRoleContext.get())
      ? currentAppSession()
      : {};
    return {
      ...state,
      accountId,
      accountEmail,
      accountName,
      applicantContext,
      loggedInRole,
      advisorUsername,
      productType,
      rolesId,
      teamId,
      currentLoanRoleContext,
      token,
      expiration: expiryDateFromLocalStorage,
    };
  })
  .case(handleInitCurrentLoanContext, (state, payload) => {
    /**
     * Set the current context in  local storage
     * and redux state
     */
    authenticatedApplicantContext.currentLoanRoleContext.set(window.btoa(JSON.stringify(payload)));

    /** the current context must be destructured. Since the loanrole id is an
     * array. Why is the loan role id an array? We have to ask David*/
    const {
      loanAppId,
      loanType,
      loanRoleAccountId,
      loanRoleId,
      teamId,
      accountEmail,
      loanProductType
    } = currentAppSession();
    const currentLoanRoleContext = {
      loanAppId,
      loanRoleId,
      loanRoleAccountId,
      loanType,
      teamId,
      accountEmail,
      loanProductType
    }

    return {
      ...state,
      currentLoanRoleContext,
    };
  })
  .case(handleLogout, (state) => {
    /** clear local storage and redux state */
    clearLocalStorage();
    return {
      ...state,
      accountId: null,
      accountEmail: null,
      accountName: null,
      applicantContext: {
        accountId: null,
        accountEmail: null,
        teamId: null
      },
      loggedInRole: null,
      token: null,
      expiration: null,
      advisorUsername: null,
      signupType: null,
      rolesId: null,
      teamId: null,
      productType: null
    };
  })
  .case(handleRefreshTokens, (state) => {
    /** get local storage values and append to redux state */
    return {
      ...state,
      token: authentication.token.get(),
      expiration: authentication.expire.get(),
    };
  })
  .default((state) => state);

export default reducer;
